import React, { useState, forwardRef } from 'react';
import Table from '../common/Table';
import {
  EyeIcon,
  PencilIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  FunnelIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

const ComplaintsTable = forwardRef(({ 
  complaints = [],
  loading = false,
  
  onView, 
  onEdit, 
  onDelete,
  getStatusColor,
  getPriorityColor,
  getComplainantName,
  getComplaintType,
  
  filters,
  onFilterChange,
  onSearchChange,
  
  purokOptions = [],
  typeOptions = [],
  statusOptions = [],
  priorityOptions = [],
  
  currentPage,
  totalItems,
  itemsPerPage,
  totalPages,
  onPageChange,
  onItemsPerPageChange,
}, ref) => {
  const [openDropdown, setOpenDropdown] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState(filters.search || '');
  const [searchTimeout, setSearchTimeout] = useState(null);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);

    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeout = setTimeout(() => {
      onSearchChange(value);
    }, 500);

    setSearchTimeout(timeout);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
        setSearchTimeout(null);
      }
      onSearchChange(searchQuery);
    }
  };

  const handleFilterChange = (field, value) => {
    onFilterChange(field, value);
  };

  const clearFilters = () => {
    onFilterChange('purok', '');
    onFilterChange('type', '');
    onFilterChange('status', '');
    onFilterChange('priority', '');
    onFilterChange('date_from', '');
    onFilterChange('date_to', '');
    onSearchChange('');
    setSearchQuery('');
  };

  const hasActiveFilters = () => {
    return Object.values(filters).some(value => value && value !== '');
  };

  const getActiveFilterCount = () => {
    return Object.values(filters).filter(value => value && value !== '').length;
  };

  const toggleFilter = () => {
    setFilterOpen(!filterOpen);
  };

  const truncateText = (text, maxLength = 50) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const formatOptionLabel = (value) => {
    if (!value) return '';
    return value.charAt(0).toUpperCase() + value.slice(1).replace(/_/g, ' ');
  };

  const formatStatus = (status) => {
  if (!status) return '';
  
  if (status === 'in progress' || status === 'in_progress') {
    return 'In Progress';
  }
  
  return status.charAt(0).toUpperCase() + status.slice(1);
};

const formatPriority = (priority) => {
  if (!priority) return '';
  return priority.charAt(0).toUpperCase() + priority.slice(1);
};

  const columns = [
    {
      key: 'complaint',
      label: 'Complaint',
      render: (_, complaint) => (
        <div className="max-w-xs">
          <div className="text-sm font-medium text-gray-900 truncate" title={complaint?.description || 'No description provided'}>
            {truncateText(complaint?.description || 'No description provided', 60)}
          </div>
          <div className="flex items-center gap-2 mt-1">
            {complaint?.complaint_ref_no && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-large bg-blue-50 text-blue-700 font-mono truncate max-w-[120px]" title={complaint.complaint_ref_no}>
                {complaint.complaint_ref_no}
              </span>
            )}
            <span className="text-xs text-gray-500 truncate max-w-[100px]" title={getComplainantName(complaint)}>
              by {truncateText(getComplainantName(complaint), 20)}
            </span>
          </div>
        </div>
      ),
    },
    {
      key: 'type',
      label: 'Type',
      render: (_, complaint) => (
        <span className="text-sm text-gray-900 capitalize truncate block max-w-[120px]" title={getComplaintType(complaint)}>
          {truncateText(getComplaintType(complaint), 20)}
        </span>
      ),
    },
    {
      key: 'purok',
      label: 'Purok',
      render: (_, complaint) => (
        <span className="text-sm text-gray-900 truncate block max-w-[100px]" title={complaint?.purok || 'N/A'}>
          {complaint?.purok || 'N/A'}
        </span>
      ),
    },
     {
    key: 'status',
    label: 'Status',
    render: (_, complaint) => (
      <span
        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
          complaint?.status || 'pending'
        )} truncate max-w-[100px]`}
        title={complaint?.status || 'pending'}
      >
        {formatStatus(complaint?.status || 'pending')}
      </span>
    ),
  },
  {
    key: 'priority',
    label: 'Priority',
    render: (_, complaint) => (
      <span
        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(
          complaint?.priority || 'low'
        )} truncate max-w-[80px]`}
        title={complaint?.priority || 'low'}
      >
        {formatPriority(complaint?.priority || 'low')}
      </span>
    ),
  },
    {
      key: 'created_at',
      label: 'Date',
      render: (_, complaint) => (
        <span className="text-sm text-gray-500 truncate block max-w-[100px]" title={complaint?.created_at ? new Date(complaint.created_at).toLocaleDateString() : 'No date'}>
          {complaint?.created_at ? new Date(complaint.created_at).toLocaleDateString() : 'No date'}
        </span>
      ),
    },
    {
      key: 'actions',
      label: '',
      render: (_, complaint) => (
        <div className="flex items-center justify-end space-x-2">
          <button
            onClick={() => onView(complaint)}
            className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50 transition-all duration-200 hover:scale-110"
            title="View"
          >
            <EyeIcon className="h-4 w-4" />
          </button>
          <button
            onClick={() => onEdit(complaint)}
            className="text-yellow-600 hover:text-yellow-900 p-1 rounded hover:bg-yellow-50 transition-all duration-200 hover:scale-110"
            title="Edit"
          >
            <PencilIcon className="h-4 w-4" />
          </button>
          <button
            onClick={() => onDelete(complaint)}
            className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50 transition-all duration-200 hover:scale-110"
            title="Delete"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden border border-gray-200">
      {/* Header with Search & Filter */}
      <div className="flex justify-between items-center px-4 py-3 border-b">
        <h2 className="font-semibold text-gray-800 flex items-center gap-2">
          Complaints
          {loading && (
            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
              Loading...
            </span>
          )}
         
        </h2>
        
        <div className="flex items-center gap-2">
          <div className="relative w-[24rem]">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              onKeyDown={handleSearchKeyDown}
              placeholder="Search complaints..."
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Filter Button */}
          <button
            onClick={toggleFilter}
            className={`flex items-center gap-1 px-5 py-2 border rounded-lg text-sm transition whitespace-nowrap
              ${filterOpen
                ? "border-blue-500 text-blue-600 bg-blue-50"
                : hasActiveFilters()
                  ? "border-blue-500 text-blue-600 bg-blue-50"
                  : "border-gray-400 text-gray-600 hover:bg-gray-50"
              }`}
          >
            <FunnelIcon
              className={`h-5 w-5 ${filterOpen || hasActiveFilters() ? "text-blue-600" : "text-gray-500"}`}
            />
            <span>Filter</span>
           
          </button>
        </div>
      </div>

      {/* Filter Section */}
      {filterOpen && (
        <div className="px-4 py-4 border-b bg-gray-50">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            {/* Purok Filter  */}
            <div className="flex flex-col relative">
              <label className="text-sm font-semibold text-gray-800 mb-1">
                Purok
              </label>
              <div className="relative">
                <select
                  value={filters.purok || ''}
                  onChange={(e) => handleFilterChange('purok', e.target.value)}
                  onClick={() => setOpenDropdown(prev => prev === 'purok' ? null : 'purok')}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm 
                       focus:border-blue-500 focus:ring-blue-500 appearance-none pr-8"
                >
                  <option value="">All Puroks</option>
                  {purokOptions.map(purok => (
                    <option key={purok} value={purok}>
                      {purok}
                    </option>
                  ))}
                </select>
                {openDropdown === 'purok' ? (
                  <ChevronUpIcon className="w-4 h-4 text-gray-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                ) : (
                  <ChevronDownIcon className="w-4 h-4 text-gray-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                )}
              </div>
            </div>

            {/* Type Filter */}
            <div className="flex flex-col relative">
              <label className="text-sm font-semibold text-gray-800 mb-1">
                Type
              </label>
              <div className="relative">
                <select
                  value={filters.type || ''}
                  onChange={(e) => handleFilterChange('type', e.target.value)}
                  onClick={() => setOpenDropdown(prev => prev === 'type' ? null : 'type')}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm 
                       focus:border-blue-500 focus:ring-blue-500 appearance-none pr-8"
                >
                  <option value="">All Types</option>
                  {typeOptions.map(type => (
                    <option key={type} value={type}>
                      {formatOptionLabel(type)}
                    </option>
                  ))}
                </select>
                {openDropdown === 'type' ? (
                  <ChevronUpIcon className="w-4 h-4 text-gray-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                ) : (
                  <ChevronDownIcon className="w-4 h-4 text-gray-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                )}
              </div>
            </div>

            {/* Status Filter*/}
            <div className="flex flex-col relative">
              <label className="text-sm font-semibold text-gray-800 mb-1">
                Status
              </label>
              <div className="relative">
                <select
                  value={filters.status || ''}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  onClick={() => setOpenDropdown(prev => prev === 'status' ? null : 'status')}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm 
                       focus:border-blue-500 focus:ring-blue-500 appearance-none pr-8"
                >
                  <option value="">All Status</option>
                  {statusOptions.map(status => (
                    <option key={status} value={status}>
                      {formatOptionLabel(status)}
                    </option>
                  ))}
                </select>
                {openDropdown === 'status' ? (
                  <ChevronUpIcon className="w-4 h-4 text-gray-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                ) : (
                  <ChevronDownIcon className="w-4 h-4 text-gray-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                )}
              </div>
            </div>

            {/* Priority Filter */}
            <div className="flex flex-col relative">
              <label className="text-sm font-semibold text-gray-800 mb-1">
                Priority
              </label>
              <div className="relative">
                <select
                  value={filters.priority || ''}
                  onChange={(e) => handleFilterChange('priority', e.target.value)}
                  onClick={() => setOpenDropdown(prev => prev === 'priority' ? null : 'priority')}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm 
                       focus:border-blue-500 focus:ring-blue-500 appearance-none pr-8"
                >
                  <option value="">All Priorities</option>
                  {priorityOptions.map(priority => (
                    <option key={priority} value={priority}>
                      {formatOptionLabel(priority)}
                    </option>
                  ))}
                </select>
                {openDropdown === 'priority' ? (
                  <ChevronUpIcon className="w-4 h-4 text-gray-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                ) : (
                  <ChevronDownIcon className="w-4 h-4 text-gray-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                )}
              </div>
            </div>
          </div>

          {/* Date Range and Clear Button */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="flex flex-col">
              <label className="text-sm font-semibold text-gray-800 mb-1">
                Date From
              </label>
              <input
                type="date"
                value={filters.date_from || ''}
                onChange={(e) => handleFilterChange('date_from', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm 
                     focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <div className="flex flex-col">
              <label className="text-sm font-semibold text-gray-800 mb-1">
                Date To
              </label>
              <input
                type="date"
                value={filters.date_to || ''}
                onChange={(e) => handleFilterChange('date_to', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm 
                     focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <div className="hidden md:block"></div>

            <div className="flex items-end justify-end">
              <button
                onClick={clearFilters}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <XMarkIcon className="w-4 h-4 mr-2" />
                Clear All Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {!loading && complaints.length === 0 ? (
        <div className="text-center py-12">
          <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">No complaints found</h3>
          <p className="mt-2 text-sm text-gray-500">
            {hasActiveFilters() 
              ? "Try adjusting your filters or search criteria."
              : "Get started by creating a new complaint."}
          </p>
        </div>
      ) : (
        <div className="p-0">
          <Table
            noContainer={true}
            columns={columns}
            data={complaints}
            getRowKey={(complaint) => complaint?.id || `complaint-${Math.random()}`}
            isLoading={loading}
            emptyMessage="No complaints found"
            pagination={{
              currentPage,
              lastPage: totalPages,
              perPage: itemsPerPage,
              total: totalItems,
            }}
            onPageChange={onPageChange}
            paginationProps={{
              onPerPageChange: onItemsPerPageChange,
              isLoading: loading,
              showSizeSelector: true,
              sizeOptions: [5, 10, 15, 25, 50],
            }}
          />
        </div>
      )}
    </div>
  );
});

ComplaintsTable.displayName = 'ComplaintsTable';

export default ComplaintsTable;